import React, { ChangeEvent, useState } from 'react';
import { TrackEvent } from '@stores/source';
import { SelectV1 } from '@ui-library/select';
import moment from 'moment';
import Table from '@ui-library/table';
import { Flex } from '@ui-library/misc';
import { Search } from '@ui-library/input';
import Text from '@ui-library/table/components/text';
import Tooltip from '@ui-library/tooltip';
import { InfoCircleOutlined } from '@ant-design/icons';
import classes from './events.module.scss';
import { SORT_OPTIONS, SORT_ORDER } from './hooks';
import { getPaginationTotal, PER_PAGE } from './utils';

interface Props {
  events?: TrackEvent[];
  hasMoreEvents: boolean;
  setFilters: {
    setSortBy: (value: string) => void;
    setSortOrder: (value: string) => void;
    setSearch: (value: string) => void;
    setPage: (value: number) => void;
  };
  filters: {
    sortBy: string;
    sortOrder: string;
    search: string;
  };
  loading: boolean;
  page: number;
  onPageChange: (page: number, perPage: number) => void;
}

const getTableData = (tableData?: { name: string; count: number; lastSent: number }[]) =>
  tableData?.map((error) => ({
    id: error.name,
    column1: <Text name={error.name} />,
    column2: <Text name={error.count?.toLocaleString()} />,
    column3: <Text name={moment(error.lastSent).fromNow()} />,
  }));

const columns = [
  {
    title: (
      <Flex>
        <span>EVENT NAME</span>
        <Tooltip title="Note: The events in this table represent 'Track' events only">
          <span className="m-l-sm">
            <InfoCircleOutlined />
          </span>
        </Tooltip>
      </Flex>
    ),
    dataIndex: 'column1',
    width: '40%',
  },
  {
    title: 'COUNT',
    dataIndex: 'column2',
    width: '30%',
  },
  {
    title: 'LAST SEEN',
    dataIndex: 'column3',
    width: '30%',
  },
];

export default ({
  events,
  filters: { sortBy, sortOrder, search },
  hasMoreEvents,
  setFilters: { setSortBy, setSortOrder, setSearch, setPage },
  loading,
  page,
  onPageChange,
}: Props) => {
  const [input, setInput] = useState(search);

  if (events && events.length === 0 && !search) {
    return null;
  }

  const onSearch = () => setSearch(input);

  const onChangeSortBy = (value: string) => setSortBy(value);

  const onChangeSortOrder = (value: string) => setSortOrder(value);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => setInput(e.target.value);

  const handlePageChange = (newPage: number, perPage = PER_PAGE) => {
    onPageChange(newPage, perPage);
    setPage(newPage);
  };

  return (
    <>
      <Flex alignItems="start" justifyContent="start" className={classes.track_filters}>
        <Search
          className={classes.search}
          placeholder="Search event names"
          value={input}
          onChange={onChange}
          onSearch={onSearch}
          disabled={loading}
        />
        <div className={classes.sort_by}>
          <SelectV1
            options={SORT_OPTIONS}
            onChange={onChangeSortBy}
            value={sortBy}
            disabled={loading}
          />
        </div>
        <div className={classes.sort_order}>
          <SelectV1
            options={SORT_ORDER}
            onChange={onChangeSortOrder}
            value={sortOrder}
            disabled={loading}
          />
        </div>
      </Flex>
      <Table
        columns={columns}
        dataSource={getTableData(events)}
        pagination={{
          current: page,
          total: getPaginationTotal(events, hasMoreEvents),
          onChange: handlePageChange,
          pageSize: PER_PAGE,
          showSizeChanger: false,
          hideOnSinglePage: true,
        }}
        loading={loading}
      />
    </>
  );
};
